import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import './Scheduler.css';
import LocationSelection from './LocationSelection';
import ServiceSelection from './ServiceSelection';
import DateSelection from './DateSelection';
import PersonalInfoForm from './PersonalInfoForm';
import PhoneValidationStep from './PhoneValidationStep';
import BookingRecap from './BookingRecap';
import VisitTypeSelection from './VisitTypeSelection';
import StepIndicator from './StepIndicator';
import SuccessScreen from './SuccessScreen';
import CancelledScreen from './CancelledScreen';
import PoweredByCinch from './PoweredByCinch';
import { submitBooking, getAvailability } from '../utils/api';
import { useBranding } from '../contexts/BrandingContext';
import { useCalendar } from '../contexts/CalendarContext';
import { useSession } from '../contexts/SessionContext';
import { useLeadSalvage } from '../contexts/LeadSalvageContext';
import {
  BOOKING_FAILURE_REASONS,
  STEPS,
  createStepTracker,
  isWidgetShown,
  onWidgetShown,
  reportWidgetLanding,
  stepIdForScreen,
  trackBookingCompleted,
  trackBookingFailed,
  trackBookingSubmitted,
} from '../analytics';
import { updateSession } from '../utils/api';
import { deleteCookie } from '../utils/cookies';
import {
  getContactFields,
  getExtraFields,
  getPossessionModel,
  getPossessionFields,
  fieldStateKey,
  isFieldRequired,
} from '../utils/dataFields';
// CIN-6879: Reserve with Google requires the store address above the fold on
// EVERY landing page. This header is the only one the widget renders (two JSX
// copies: the cancelled/expired screen and the wizard, which also covers the
// step-6 success screen), so rendering the address here covers every step.
import { formatAddressSingleLine } from '../utils/address';
import {
  availabilityKey,
  displayProgress,
  CONFIRM_TIME_MESSAGE,
  isNoSharedVisitTypeError,
  isVisitType,
  offeredVisitTypes,
  stepShown,
  DROPOFF_RETRY_CODES,
  DROPOFF_UNAVAILABLE_MESSAGE,
  NO_SHARED_VISIT_TYPE_MESSAGE,
  VISIT_TYPE_STEP_TITLE,
} from '../utils/visitType';
import { styled } from '@mui/material/styles';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';

// Styled progress bar component
const BorderLinearProgress = styled(LinearProgress)(({ theme, primarycolor }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#e5e7eb', // light gray
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: primarycolor || '#1a90ff',
  },
}));

// Whole days from today to the appointment. Describes how far ahead consumers
// book without carrying the appointment time itself.
const daysUntilAppointment = (isoDateTime) => {
  if (!isoDateTime) return undefined;
  const appointment = new Date(isoDateTime);
  if (Number.isNaN(appointment.getTime())) return undefined;
  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((startOfDay(appointment) - startOfDay(new Date())) / 86400000);
};

// The pickers use '' for "nothing chosen" and 'new' for "create one", so an
// existing record is anything else.
const isExistingSelection = (id) => !!id && id !== 'new';

const Scheduler = () => {
  const { branding, widgetConfig } = useBranding();
  const { calendarId, locations, validatePhone, locationIdParam, fields, enableWaiterDropoff } = useCalendar();
  // CIN-6274: route real close entry points (X button + overlay click) through
  // the lead-salvage gate. closeWidget() stays available on useCalendar() as the
  // raw close, which the salvage flow itself invokes after show/dismiss/submit.
  const { attemptClose } = useLeadSalvage();
  const {
    sessionId,
    deepLinkLandsOnDateTime,
    currentStep,
    setCurrentStep,
    selectedLocation,
    selectedServices,
    selectedTimeSlot,
    setSelectedTimeSlot,
    personalInfo,
    phoneVerified,
    selectedCustomerId,
    selectedVehicleId,
    vehicleCustomerRefId,
    vehiclePossessionId,
    cancelled,
    schedule,
    setSchedule,
    // CIN-7181: Wait / Drop-off choice (all absent -> feature off behaviour).
    visitType,
    setVisitType,
    visitTypeOffered,
    setVisitTypeOffered,
    visitTypeStepActive,
    setVisitTypeStepActive,
    setVisitTypeError,
    setAvailabilityPrefetch,
  } = useSession();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Location is skipped outright on a single-location calendar and on a link
  // that names the location. Three render guards below and the analytics step
  // order all turn on this one fact.
  const locationStepSkipped = locations.length === 1 || !!locationIdParam;

  // What the consumer had chosen when they left a step. Nothing a consumer
  // typed goes in — a comment is reported as a yes/no, never as text (CIN-7080).
  const stepDetailsFor = useCallback(
    (stepId) => {
      switch (stepId) {
        case STEPS.LOCATION:
          return { calendar_location_id: selectedLocation?.id, used_search: false };
        case STEPS.SERVICES:
          return {
            service_ids: selectedServices.map((service) => service.id),
            service_count: selectedServices.length,
            was_preselected: false,
          };
        case STEPS.DATE_TIME:
          return { days_until_appointment: daysUntilAppointment(selectedTimeSlot), weeks_paged: 0 };
        case STEPS.PHONE_VERIFICATION:
          return { phone_verified: !!phoneVerified };
        case STEPS.PERSONAL_INFO:
          return {
            is_returning_customer: isExistingSelection(selectedCustomerId),
            used_existing_vehicle: isExistingSelection(selectedVehicleId),
            left_comment: !!(personalInfo.comments || '').trim(),
          };
        default:
          return {};
      }
    },
    [selectedLocation, selectedServices, selectedTimeSlot, phoneVerified, selectedCustomerId, selectedVehicleId, personalInfo],
  );

  // One observer for every step change, driven by what is RENDERED rather than
  // by the callers that move the consumer — session restore, three auto-advance
  // effects, the deep-link resolver, Back and Next all do. A funnel that missed
  // any of them would read as a drop-off that never happened. The first settled
  // step is also the landing step booking_widget_opened waits for (CIN-7080).
  const stepTrackerRef = useRef(null);
  const reportedLandingRef = useRef(false);
  // An embed created up front boots this whole component hidden on every page
  // view of the host site. Nothing is reported until the embed script says the
  // widget is on screen; when it does, this flips and the effect below runs
  // against whatever step is rendered THEN — which is both the first step the
  // consumer sees and the landing step (CIN-7080).
  const [widgetShown, setWidgetShown] = useState(isWidgetShown);
  useEffect(() => onWidgetShown(() => setWidgetShown(true)), []);

  useEffect(() => {
    if (!widgetShown) return;
    // Step 1 renders for a tick before the auto-advance effect moves past it on
    // a calendar with no location step; reporting it would invent a screen the
    // consumer never saw.
    if (locationStepSkipped && currentStep === 1) return;
    // Same idea for a Reserve with Google service link: the screens before
    // Date & Time render for a frame each on the way there. Skipping them is
    // what leaves Date & Time as the FIRST step reported, which is what makes
    // the tracker call it a deep_link landing rather than a forward arrival.
    if (deepLinkLandsOnDateTime && currentStep < 3) return;

    const stepId = stepIdForScreen({ currentStep, validatePhone, phoneVerified });
    if (!stepId) return;

    if (!stepTrackerRef.current) {
      stepTrackerRef.current = createStepTracker();
    }
    if (!reportedLandingRef.current) {
      reportedLandingRef.current = true;
      reportWidgetLanding({ landingStep: stepId });
    }
    stepTrackerRef.current.report({ stepId, detailsFor: stepDetailsFor });
  }, [widgetShown, currentStep, validatePhone, phoneVerified, locationStepSkipped, deepLinkLandsOnDateTime, stepDetailsFor]);
  // CIN-7181: Services Next is resolving which visit types are offered.
  const [resolvingVisitType, setResolvingVisitType] = useState(false);
  // CIN-7181: shown on Date & Time when /book rejected the drop-off time.
  const [dateStepNotice, setDateStepNotice] = useState(null);
  // Picking a new time answers the notice.
  useEffect(() => {
    if (selectedTimeSlot) setDateStepNotice(null);
  }, [selectedTimeSlot]);
  // The choice step is shown only when the feature is on and both types are offered.
  const visitTypeStepShown = stepShown(enableWaiterDropoff, visitTypeOffered || []);
  // On the choice step (a sub-state of step 2) Back is always available, even
  // on a single-location calendar where Services itself has no Back.
  const onChoiceStep = currentStep === 2 && !!visitTypeStepActive;
  // CIN-7181: where the visitor is NOW, read by resolveVisitTypeAfterServices
  // when its availability call lands (its closure holds the click-time values).
  // Refreshed after every commit (no deps), before any later await resumes.
  const resolveRequestRef = useRef(0);
  const visitorPositionRef = useRef({ onServices: false, key: null });
  const visitorOnServices = currentStep === 2 && !onChoiceStep;
  const visitorKey = availabilityKey(selectedLocation?.id, selectedServices.map(s => s.id));
  useLayoutEffect(() => {
    visitorPositionRef.current = { onServices: visitorOnServices, key: visitorKey };
  });
  // CIN-7181: the choice step has no heading of its own; the modal title
  // becomes its question, so move focus there when the visitor moves onto it
  // from inside the widget (Services Next, Back from Date & Time, Review's
  // edit). Those transitions arm focusTitleOnChoiceRef; a mount, a cookie
  // restore or Date & Time's guard do not, so loading the widget never steals
  // focus. preventScroll: the widget is iframed on shop sites, and focus()
  // would otherwise scroll the HOST page to the iframe.
  const stepTitleRef = useRef(null);
  const focusTitleOnChoiceRef = useRef(false);
  useEffect(() => {
    // Consumed on every change of step, so an arm that did not land on the
    // choice step can't fire on a later, non-visitor arrival.
    const wanted = focusTitleOnChoiceRef.current;
    focusTitleOnChoiceRef.current = false;
    if (onChoiceStep && wanted && stepTitleRef.current) {
      stepTitleRef.current.focus({ preventScroll: true });
    }
  }, [onChoiceStep]);
  const hideBackOnServices = currentStep === 2 && !onChoiceStep && locationStepSkipped;
  // CIN-6879: single-line store address for the modal header. Empty string when
  // no location is resolved yet (multi-location step 1, where the list itself
  // shows every address) or when the location carries no address fields, so the
  // node is simply absent rather than rendered blank.
  const locationAddress = formatAddressSingleLine(selectedLocation);
  const [bookingError, setBookingError] = useState(null);

  const handleBookingSubmit = async (bookingData) => {
    return await submitBooking(calendarId, {
      session_id: sessionId,
      ...bookingData
    });
  };

  const totalSteps = 5;
  const stepTitles = {
    1: "Location",
    2: "Services",
    3: "Date & Time",
    4: "Personal Information",
    5: "Review & Confirm",
    6: "Success"
  };


  // CIN-7181: after Services (feature on) ask availability which visit types
  // the selection offers - the SAME no-start_date call Date & Time makes, whose
  // response Date & Time then reuses. Both -> the choice step (current_step
  // stays 2); one -> skip it with that type preselected; none -> Date & Time's
  // existing no-availability copy; NO_SHARED_VISIT_TYPE -> stay on Services.
  //
  // Only the latest request, still answering the selection the visitor is on
  // Services with, may act: a response that lands after Back (even Back and
  // forward again), or after the selection changed, is dropped so it can't
  // navigate away from where the visitor now is or open the choice step for
  // an old selection.
  const resolveVisitTypeAfterServices = async () => {
    const locationId = selectedLocation?.id;
    const serviceIds = selectedServices.map(s => s.id);
    const key = availabilityKey(locationId, serviceIds);
    resolveRequestRef.current += 1;
    const requestId = resolveRequestRef.current;
    const isCurrent = () => (
      requestId === resolveRequestRef.current
      && visitorPositionRef.current.onServices
      && visitorPositionRef.current.key === key
    );
    setResolvingVisitType(true);
    setVisitTypeError(null);
    try {
      const response = await getAvailability(calendarId, sessionId, locationId, serviceIds);
      if (!isCurrent()) return;
      const offered = offeredVisitTypes(response);
      setAvailabilityPrefetch({ key, response });
      setVisitTypeOffered(offered);
      if (offered.length === 2) {
        // Back/forward keeps the visitor's choice when it is still offered.
        if (!offered.includes(visitType)) setVisitType(null);
        focusTitleOnChoiceRef.current = true;
        setVisitTypeStepActive(true);
      } else if (offered.length === 1) {
        if (visitType !== offered[0]) {
          // A slot picked for the other type is not on this type's grid.
          setSelectedTimeSlot(null);
          setVisitType(offered[0]);
        }
        setCurrentStep(3);
      } else {
        if (visitType !== null) {
          setSelectedTimeSlot(null);
          setVisitType(null);
        }
        setCurrentStep(3);
      }
    } catch (err) {
      if (!isCurrent()) return;
      if (isNoSharedVisitTypeError(err)) {
        setVisitTypeError(NO_SHARED_VISIT_TYPE_MESSAGE);
      } else {
        // Exactly as today: Date & Time renders its own load error.
        setCurrentStep(3);
      }
    } finally {
      // A superseded request leaves the flag to the request that replaced it
      // (or to the Back that cancelled it).
      if (requestId === resolveRequestRef.current) setResolvingVisitType(false);
    }
  };

  // CIN-7181: leaving Services abandons any in-flight resolution.
  const cancelVisitTypeResolution = () => {
    resolveRequestRef.current += 1;
    setResolvingVisitType(false);
  };

  const handleNext = () => {
    if (onChoiceStep) {
      setVisitTypeStepActive(false);
      setCurrentStep(3);
      return;
    }
    if (currentStep === 2 && enableWaiterDropoff) {
      resolveVisitTypeAfterServices();
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  // CIN-7181: Services (from the choice step's context, or Date & Time's
  // 'Change'). Never lands on the choice step.
  const goToServices = () => {
    setDateStepNotice(null);
    if (visitTypeStepActive) setVisitTypeStepActive(false);
    setCurrentStep(2);
  };

  // CIN-7181: back onto the choice step with the current answer preselected.
  const goToVisitTypeStep = () => {
    focusTitleOnChoiceRef.current = true;
    setCurrentStep(2);
    setVisitTypeStepActive(true);
  };

  const handleBack = () => {
    setDateStepNotice(null);
    cancelVisitTypeResolution();
    // CIN-7181: choice step -> Services; Date & Time -> the choice step when it was shown.
    if (onChoiceStep) {
      setVisitTypeStepActive(false);
      return;
    }
    if (currentStep === 3 && visitTypeStepShown) {
      goToVisitTypeStep();
      return;
    }
    if (currentStep > 1) {
      // Skip location step if there's only one location or locationId was provided
      if (currentStep === 2 && locationStepSkipped) {
        // Don't go back to step 1
        return;
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return selectedLocation !== null;
      case 2:
        if (onChoiceStep) return isVisitType(visitType);
        return selectedServices.length > 0 && !resolvingVisitType;
      case 3:
        return selectedTimeSlot !== null;
      case 4: {
        // If phone validation is required and phone is not verified yet
        if (validatePhone && !phoneVerified) {
          // Only need phone number to be entered
          const digits = personalInfo.phone ? personalInfo.phone.replace(/[^\d]/g, '') : '';
          return digits.length === 10 || digits.length === 11; // allow leading 1 for US country code
        }
        const hasValue = (key) => personalInfo[key] && String(personalInfo[key]).trim() !== '';
        // When an existing customer / possession is chosen, its section is hidden and the
        // record is sent by id — so those fields aren't required (mirrors the backend, which
        // only requires contact fields when no customer_id/customer_ref_id is provided).
        const contactShown = !selectedCustomerId || selectedCustomerId === 'new';
        const possessionIsExisting = selectedVehicleId && selectedVehicleId !== 'new';
        const configuredValid = [
          ...(contactShown ? getContactFields(fields) : []),
          ...(possessionIsExisting ? [] : getPossessionFields(fields)),
          ...getExtraFields(fields),
        ].every((field) => {
          if (!isFieldRequired(field)) return true;
          if (field.id === 'phone') {
            const digits = personalInfo.phone ? personalInfo.phone.replace(/[^\d]/g, '') : '';
            return digits.length === 10 || digits.length === 11; // allow leading 1 for US country code
          }
          return hasValue(fieldStateKey(field));
        });
        // CIN-6385: email is optional, but if the customer typed one it must be a valid
        // format — otherwise the /book serializer 400s at Confirm. Gate Next until email
        // is blank or well-formed.
        const trimmedEmail = (personalInfo.email || '').trim();
        const emailValid = trimmedEmail === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
        return configuredValid && emailValid;
      }
      case 5:
        return true;
      default:
        return false;
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      // CIN-6274: gate the close so an abandoning consumer sees the salvage modal.
      attemptClose();
    }
  };

  const renderProgressIndicator = () => {
    const progressType = widgetConfig?.progress_indicator || 'stepper';

    if (progressType === 'none') {
      return null;
    }

    // CIN-7181: display position only - the choice step is shown as step 3 of
    // 6 when it appears; navigation keeps the internal 5-step numbering.
    const { step: displayStep, total: displayTotal } = displayProgress({
      currentStep,
      visitTypeStepActive: onChoiceStep,
      shown: visitTypeStepShown,
    });
    const progress = (displayStep / displayTotal) * 100;

    switch (progressType) {
      case 'bar':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 16px' }}>
            <BorderLinearProgress
              variant="determinate"
              value={progress}
              primarycolor={branding.primary_color}
              sx={{ flex: 1 }}
            />
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#333', minWidth: '40px' }}>
              {Math.round(progress)}%
            </span>
          </div>
        );
      case 'line':
        return (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              backgroundColor: '#e5e7eb', // light gray
              '& .MuiLinearProgress-bar': {
                backgroundColor: branding.primary_color
              }
            }}
          />
        );
      case 'stepper':
      default:
        return (
          <StepIndicator
            currentStep={displayStep}
            totalSteps={displayTotal}
          />
        );
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <LocationSelection />;
      case 2:
        if (onChoiceStep) {
          return (
            <VisitTypeSelection
              onEditLocation={locations.length === 1 || !!locationIdParam ? undefined : () => {
                setVisitTypeStepActive(false);
                setCurrentStep(1);
              }}
              onEditServices={goToServices}
            />
          );
        }
        return <ServiceSelection />;
      case 3:
        return <DateSelection onChangeServices={goToServices} />;
      case 4:
        // Show phone validation if validatePhone is true and phone not verified
        if (validatePhone && !phoneVerified) {
          return <PhoneValidationStep />;
        }
        // Otherwise show the full personal info form
        return <PersonalInfoForm />;
      case 5:
        return <BookingRecap onEditVisitType={visitTypeStepShown ? goToVisitTypeStep : undefined} />;
      case 6:
        return <SuccessScreen />;
      default:
        return null;
    }
  };

  // If the appointment is cancelled, show the cancelled screen
  if (cancelled) {
    // Check if the appointment is expired
    const isExpired = schedule?.appointment_naive && new Date(schedule.appointment_naive) < new Date();

    return (
      <div className="modal-overlay" onClick={handleOverlayClick}>
        <div className="modal-content">
          <div className="modal-header">
            {branding?.logo_url && (
              <div className="modal-logo">
                <img src={branding.logo_url} alt="Company Logo" />
              </div>
            )}
            <div className="modal-title-group">
              <h2>Appointment {isExpired ? 'Expired' : 'Cancelled'}</h2>
              {/* CIN-6784: merchant name, matching the Reserve with Google entity feed. */}
              {branding?.company_name && (
                <div className="modal-company-name" data-testid="modal-company-name">
                  {branding.company_name}
                </div>
              )}
              {/* CIN-6879: store address, directly under the merchant name. */}
              {locationAddress && (
                <div className="modal-location-address" data-testid="modal-location-address">
                  {locationAddress}
                </div>
              )}
            </div>
            <button className="close-button" onClick={attemptClose}>×</button>
          </div>
          <div className="modal-body">
            <CancelledScreen />
          </div>
          <PoweredByCinch />
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          {branding?.logo_url && (
            <div className="modal-logo">
              <img src={branding.logo_url} alt="Company Logo" />
            </div>
          )}
          <div className="modal-title-group">
            <h2 ref={stepTitleRef} tabIndex={-1} className="modal-step-title">{
              currentStep === 4 && validatePhone && !phoneVerified
                ? "Phone Verification"
                : onChoiceStep
                  ? VISIT_TYPE_STEP_TITLE
                  : stepTitles[currentStep]
            }</h2>
            {/* CIN-6784: merchant name, matching the Reserve with Google entity feed. */}
            {branding?.company_name && (
              <div className="modal-company-name" data-testid="modal-company-name">
                {branding.company_name}
              </div>
            )}
            {/* CIN-6879: store address, directly under the merchant name. */}
            {locationAddress && (
              <div className="modal-location-address" data-testid="modal-location-address">
                {locationAddress}
              </div>
            )}
          </div>
          <button className="close-button" onClick={attemptClose}>×</button>
        </div>

        {currentStep <= totalSteps && renderProgressIndicator()}

        <div className="modal-body">
          {/* CIN-7181: the drop-off time was taken before Confirm; same visual
              as the booking-error block, above the grid so it is seen first. */}
          {dateStepNotice && currentStep === 3 && (
            <div data-testid="date-step-notice" role="alert" style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '4px',
              color: '#c00',
              textAlign: 'center'
            }}>
              {dateStepNotice}
            </div>
          )}
          {renderStep()}
          {/* CIN-6620: the confirm-step block message (e.g. the vehicle
              double-booking 409) is rendered here verbatim from the API body. */}
          {bookingError && currentStep === 5 && (
            <div data-testid="booking-error" style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '4px',
              color: '#c00',
              textAlign: 'center'
            }}>
              {bookingError}
            </div>
          )}
        </div>

        {currentStep <= totalSteps && (
          <div className="modal-footer">
            <div className="modal-actions" style={{
              justifyContent: (currentStep === 1 || hideBackOnServices) ? 'flex-end' : 'space-between'
            }}>
              {currentStep > 1 && !hideBackOnServices && (
                <button
                  className="action-button secondary"
                  onClick={handleBack}
                >
                  Back
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  className="action-button primary"
                  onClick={handleNext}
                  disabled={!isStepValid()}
                >
                  Next
                </button>
              ) : (
                <button
                  className="action-button primary"
                  onClick={async () => {
                    // CIN-7181: a session restored past Date & Time from before
                    // drop-offs were turned on carries no visit type, which the
                    // backend 400s (VISIT_TYPE_REQUIRED). Send the visitor to
                    // Date & Time, whose guard resolves the type from availability.
                    if (enableWaiterDropoff && !isVisitType(visitType)) {
                      setSelectedTimeSlot(null);
                      setDateStepNotice(CONFIRM_TIME_MESSAGE);
                      setCurrentStep(3);
                      return;
                    }
                    setIsSubmitting(true);
                    setBookingError(null);
                    setDateStepNotice(null);

                    const possessionModel = getPossessionModel(fields);
                    const isVehicle = possessionModel === 'vehicle';
                    // Possession field values keyed by field id (used for animal / real_estate).
                    const possessionValues = getPossessionFields(fields).reduce((acc, field) => {
                      const key = fieldStateKey(field);
                      acc[key] = personalInfo[key] || null;
                      return acc;
                    }, {});
                    // Generic label + summary for the booked possession (recap / iCal / postMessage).
                    const possessionLabel = fields?.possession_label || 'Details';
                    const possessionSummary = getPossessionFields(fields)
                      .map((field) => personalInfo[fieldStateKey(field)])
                      .filter(Boolean)
                      .join(' ');

                    const bookingData = {
                      calendar_location_id: selectedLocation.id,
                      service_ids: selectedServices.map(s => s.id),
                      date: selectedTimeSlot, // Full ISO datetime string

                      // Service details for backend
                      type: selectedServices.map(s => s.name).join(', '),
                      description: selectedServices.map(s => s.description).filter(d => d).join('; '),

                      first_name: personalInfo.first_name,
                      last_name: personalInfo.last_name,
                      phone: personalInfo.phone,
                      // CIN-6385: the /book serializer's email field is
                      // EmailField(required=False, allow_blank=True) with NO
                      // allow_null, so a blank email MUST be sent as "" (never
                      // null, which 400s). Trim so whitespace-only isn't sent
                      // as an invalid non-blank value.
                      email: (personalInfo.email || '').trim(),

                      // Possession: vehicle uses the dedicated vehicle_* fields + Motor lookups;
                      // animal / real_estate send a generic possession dict keyed by field id.
                      possession_model: possessionModel || null,
                      ...(isVehicle
                        ? {
                            vehicle_year: personalInfo.year,
                            vehicle_make: personalInfo.make,
                            vehicle_model: personalInfo.model,
                          }
                        : { possession: possessionValues }),

                      // Include selected customer and possession IDs
                      customer_id: selectedCustomerId !== '' && selectedCustomerId !== 'new' ? selectedCustomerId : null,
                      vehicle_id: isVehicle && selectedVehicleId && selectedVehicleId !== 'new' ? selectedVehicleId : null,
                      customer_ref_id: vehicleCustomerRefId !== '' && vehicleCustomerRefId !== 'new' ? vehicleCustomerRefId : null,
                      possession_id: vehiclePossessionId !== '' && vehiclePossessionId !== 'new' ? vehiclePossessionId : null,

                      // Include comments if provided
                      comments: personalInfo.comments || null,

                      // All configured extra data fields, stored on the schedule's entity_data
                      entity_data: (fields?.extra || []).reduce((acc, field) => {
                        const key = field.id.replace(/^entity_data__/, '');
                        acc[key] = personalInfo[key] || null;
                        return acc;
                      }, {}),

                      // CIN-7181: the chosen (or single offered) visit type.
                      // Feature off: the key is absent, exactly today's payload.
                      ...(enableWaiterDropoff ? { visit_type: visitType } : {}),
                    }

                    trackBookingSubmitted({
                      calendar_location_id: selectedLocation.id,
                      service_ids: bookingData.service_ids,
                      service_count: bookingData.service_ids.length,
                      days_until_appointment: daysUntilAppointment(selectedTimeSlot),
                      is_returning_customer: isExistingSelection(selectedCustomerId),
                      used_existing_vehicle: isExistingSelection(selectedVehicleId),
                    });

                    const result = await handleBookingSubmit(bookingData);
                    setIsSubmitting(false);

                    if (result.status === 429) {
                      trackBookingFailed({
                        failureReason: BOOKING_FAILURE_REASONS.RATE_LIMITED,
                        httpStatus: result.status,
                      });
                      setBookingError('You are submitting bookings too quickly. Please wait a moment and try again.');
                    } else if (result.success) {
                      trackBookingCompleted({
                        // The appointment this visit produced. QA matches a
                        // completed booking by schedule_id, and it is what
                        // joins an event to the booking record (CIN-7080).
                        schedule_id: result?.data?.schedule_id,
                        calendar_location_id: selectedLocation.id,
                        service_ids: bookingData.service_ids,
                        service_count: bookingData.service_ids.length,
                        days_until_appointment: daysUntilAppointment(selectedTimeSlot),
                        is_returning_customer: isExistingSelection(selectedCustomerId),
                        used_existing_vehicle: isExistingSelection(selectedVehicleId),
                        phone_verified: !!phoneVerified,
                      });
                      // Mark session as complete
                      await updateSession(calendarId, sessionId, { schedule: result?.data?.schedule_id });
                      
                      // CIN-5926: drop the persistent session cookie now that
                      // a schedule is booked against this session. The local
                      // sessionId state remains so Edit/Cancel work in this
                      // tab; a future page load will start a fresh session
                      // instead of silently re-attaching to a finalized one.
                      deleteCookie(`scheduler_session_${calendarId}`);
                      setSchedule({
                        id: result?.data?.id,
                        appointment_naive: selectedTimeSlot,
                        type: selectedServices.map(s => s.name).join(', '),
                        location_name: result?.data?.location_name,
                        possession_label: possessionLabel,
                        possession_summary: possessionSummary,
                        customer_first_name: personalInfo.first_name,
                        customer_last_name: personalInfo.last_name,
                        // CIN-7181: Success shows the Appointment Type row from this.
                        ...(enableWaiterDropoff ? { visit_type: visitType } : {}),
                      });
                      
                      const bookingDetails = {
                        id: result?.data?.id,
                        schedule_id: result?.data?.schedule_id,
                        appointment_date: selectedTimeSlot,
                        services: selectedServices.map(s => ({ id: s.id, name: s.name, description: s.description })),
                        location: {
                          id: selectedLocation.id,
                          name: result?.data?.location_name
                        },
                        customer: {
                          first_name: personalInfo.first_name,
                          last_name: personalInfo.last_name,
                          phone: personalInfo.phone,
                          email: personalInfo.email
                        },
                        possession: {
                          model: possessionModel || null,
                          label: possessionLabel,
                          summary: possessionSummary,
                          values: possessionValues
                        },
                        comments: personalInfo.comments
                      };

                      // Notify parent window of booking completion
                      window.parent.postMessage({
                        type: 'BOOKING_COMPLETED',
                        data: bookingDetails
                      }, '*');

                      // Redirect to success step
                      setCurrentStep(6);
                    } else if (DROPOFF_RETRY_CODES.includes(result.code)) {
                      // CIN-7181: the drop-off time filled up (or left the
                      // drop-off window) before Confirm: back to Date & Time
                      // with a fresh fetch and a pick-another notice.
                      setSelectedTimeSlot(null);
                      setAvailabilityPrefetch(null);
                      setDateStepNotice(DROPOFF_UNAVAILABLE_MESSAGE);
                      setCurrentStep(3);
                    } else {
                      // CIN-6620: prefer the server's own message (the vehicle
                      // double-booking guard returns 409 with the block copy
                      // and the shop's phone number) so the customer reads the
                      // backend string verbatim, with no client-side rewording.
                      // Deliberately does NOT advance the step: no appointment
                      // was created, so the customer stays on the recap and is
                      // never shown the success screen.
                      trackBookingFailed({
                        failureReason:
                          result.code === 'VEHICLE_DOUBLE_BOOKING'
                            ? BOOKING_FAILURE_REASONS.VEHICLE_CONFLICT
                            : BOOKING_FAILURE_REASONS.OTHER,
                        httpStatus: result.status,
                      });
                      setBookingError(result.data?.message || result.error);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
                </button>
              )}
            </div>
          </div>
        )}

        <PoweredByCinch />
      </div>
    </div>
  );
};

export default Scheduler;